import React, { useLayoutEffect, useRef } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import NeuralField from "../landing/NeuralField";
import Closing from "../landing/Closing";
import Voices from "../landing/Voices";
import PilotVoices from "./PilotVoices";
import ResultsQuad from "./ResultsQuad";
import { NdisStory, NdisPlan, NdisPairs } from "./NdisBlocks";
import { Container, Eyebrow, Display, Lead, PrimaryButton, Arrow, useReveal, useInViewPlayback } from "../landing/ui";
import { ReadingCalculator, CostCalculator, PilotResults, ChemistryExplorer, BreathingOrb } from "../landing/Interactive";
import { colors, fonts, media, prefersReducedMotion } from "../../styles/theme";
import { verticals, pilotResults, chemistry } from "../../pages/verticalContent";
import { registerFor } from "../../pages/landingContent";
import { posterFor } from "../../vid/posters";

/* ---------- hero ---------- */

const Hero = styled.section`
  position: relative;
  overflow: hidden;
  padding: 120px 0 80px;
  background: ${(p) =>
    p.$dark
      ? `radial-gradient(60% 60% at 90% 10%, rgba(184,115,51,0.3), transparent 70%), ${colors.forest}`
      : `radial-gradient(55% 55% at 95% 5%, ${p.$accent}22, transparent 70%), radial-gradient(40% 50% at 0% 40%, rgba(163,177,143,0.22), transparent 70%)`};
  color: ${(p) => (p.$dark ? colors.linen : colors.ink)};

  @media ${media.large} {
    padding: 120px 0 110px;
  }
`;

const HeroGrid = styled.div`
  display: grid;
  gap: 48px;
  /* Both columns start at the top so the button above the picture sits level with the
     crumb and eyebrow, whatever the length of the copy beneath them. */
  align-items: start;

  @media ${media.large} {
    grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
    gap: 72px;
  }
`;

const Crumb = styled(Link)`
  display: inline-flex;
  font-family: ${fonts.mono};
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${(p) => (p.$dark ? "rgba(250,247,241,0.6)" : colors.pebble)};

  &:hover {
    color: ${(p) => (p.$dark ? colors.linen : colors.ink)};
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px 28px;
  flex-wrap: wrap;
  margin-bottom: 8px;

  /* crumb above eyebrow, not beside it; the two together stand as tall as the 52px
     button above the picture, so the three sit on the same band */
  > div {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

// Two figures under the lead, where the second button used to sit.
const Figures = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 28px;
`;

const Figure = styled.div`
  flex: 1 1 200px;
  padding: 20px 22px;
  border-radius: 20px;
  border: 1px solid ${(p) => (p.$dark ? "rgba(250,247,241,0.16)" : colors.stone)};
  background: ${(p) => (p.$dark ? "rgba(250,247,241,0.05)" : "rgba(250, 247, 241, 0.7)")};

  b {
    display: block;
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: clamp(34px, 3.4vw, 46px);
    line-height: 1;
    letter-spacing: -0.04em;
    color: ${(p) => (p.$dark ? colors.linen : p.$accent)};
  }
  span {
    display: block;
    margin-top: 10px;
    font-family: ${fonts.body};
    font-size: 15px;
    line-height: 1.4;
    color: ${(p) => (p.$dark ? "rgba(250,247,241,0.75)" : colors.bark)};
  }
`;

// Sized so every hook sits on two lines and the hero, boxes included, fits a laptop screen.
const HeroTitle = styled.h1`
  font-family: ${fonts.display};
  font-weight: 300;
  font-size: clamp(44px, 5vw, 80px);
  line-height: 0.96;
  letter-spacing: -0.04em;
  margin: 16px 0 20px;

  em {
    display: block;
    font-style: normal;
    color: ${(p) => (p.$dark ? colors.copperLight : p.$accent)};
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 32px;
`;

const Ghost = styled(Link)`
  display: inline-flex;
  align-items: center;
  height: 52px;
  padding: 0 26px;
  border-radius: 999px;
  font-family: ${fonts.body};
  font-size: 16px;
  font-weight: 600;
  color: ${(p) => (p.$dark ? colors.linen : colors.ink)};
  border: 1px solid ${(p) => (p.$dark ? "rgba(250,247,241,0.35)" : colors.stone)};
  transition: border-color 0.3s ease, transform 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${(p) => (p.$dark ? colors.linen : colors.ink)};
  }
`;

// The call to action sits above the picture, right-aligned to its edge, so it lines up
// under the nav's Register interest button and the left column stays pure copy.
const Side = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 20px;
`;

const Visual = styled.div`
  position: relative;
  aspect-ratio: 4 / 5;
  max-height: 560px;
  width: 100%;
  border-radius: 32px;
  overflow: hidden;
  background: ${(p) => (p.$dark ? colors.forestSoft : colors.sand)};
  box-shadow: 0 50px 100px -50px rgba(30, 42, 34, 0.6);

  @media (max-width: 1199px) {
    aspect-ratio: 16 / 10;
  }

  video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: saturate(0.9) sepia(0.05);
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 55%, rgba(30, 42, 34, 0.55));
    pointer-events: none;
  }
`;

const VisualTag = styled.span`
  position: absolute;
  z-index: 1;
  left: 20px;
  bottom: 20px;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(250, 247, 241, 0.14);
  border: 1px solid rgba(250, 247, 241, 0.3);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  font-family: ${fonts.mono};
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.linen};
`;

const HeroVideo = ({ src, position }) => {
  const ref = useRef(null);
  useInViewPlayback(ref);
  return <video ref={ref} src={src} poster={posterFor(src)} muted loop playsInline preload="metadata" aria-hidden="true" style={position ? { objectPosition: position } : undefined} />;
};

/* ---------- stats ---------- */

const StatRow = styled.div`
  display: grid;
  gap: 16px;
  margin-top: 64px;

  @media ${media.medium} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  padding: 26px 24px;
  border-radius: 24px;
  background: ${(p) => (p.$dark ? "rgba(250,247,241,0.06)" : "rgba(250,247,241,0.75)")};
  border: 1px solid ${(p) => (p.$dark ? "rgba(250,247,241,0.12)" : colors.stone)};

  b {
    display: block;
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: clamp(40px, 4vw, 60px);
    line-height: 1;
    letter-spacing: -0.04em;
    color: ${(p) => (p.$dark ? colors.linen : colors.ink)};
  }
  p {
    margin-top: 12px;
    font-family: ${fonts.body};
    font-size: 16px;
    line-height: 1.45;
    color: ${(p) => (p.$dark ? "rgba(250,247,241,0.75)" : colors.bark)};
  }
  small {
    display: block;
    margin-top: auto;
    padding-top: 14px;
    font-family: ${fonts.mono};
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${(p) => (p.$dark ? colors.sage : colors.pebble)};
  }
`;

const StatsNote = styled.p`
  margin-top: 14px;
  font-family: ${fonts.body};
  font-size: 14px;
  color: ${(p) => (p.$dark ? "rgba(250,247,241,0.6)" : colors.pebble)};
`;

/* ---------- body sections ---------- */

const Band = styled.section`
  padding: ${(p) => p.$pad || "120px 0"};
  background: ${(p) => p.$bg || "transparent"};
`;

const SectionHead = styled.div`
  display: grid;
  gap: 20px;
  margin-bottom: 48px;

  @media ${media.medium} {
    grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr);
    align-items: end;
  }
`;

const ProblemGrid = styled.div`
  display: grid;
  gap: 16px;

  @media ${media.medium} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const ProblemCard = styled.article`
  position: relative;
  padding: 32px 28px;
  border-radius: 24px;
  background: ${colors.linen};
  border: 1px solid ${colors.stone};
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 28px;
    width: 48px;
    height: 3px;
    border-radius: 0 0 3px 3px;
    background: ${colors.terracotta};
  }
  span {
    font-family: ${fonts.mono};
    font-size: 12px;
    color: ${colors.terracotta};
  }
  h3 {
    margin: 14px 0 10px;
    font-family: ${fonts.display};
    font-weight: 400;
    font-size: 26px;
    line-height: 1.1;
    letter-spacing: -0.02em;
    color: ${colors.ink};
  }
  p {
    font-family: ${fonts.body};
    font-size: 17px;
    line-height: 1.55;
    color: ${colors.bark};
  }
`;

const QuoteBand = styled.section`
  padding: 88px 0;
  /* Every other section transition on these pages is 120px. The band is full-bleed, so the
     space has to sit outside it or the next eyebrow starts against the colored edge. */
  margin-bottom: 120px;
  background: ${(p) => p.$accent};
  color: ${colors.linen};
  overflow: hidden;
  position: relative;
`;

const BigQuote = styled.blockquote`
  position: relative;
  z-index: 1;
  max-width: 1080px;
  font-family: ${fonts.display};
  font-weight: 300;
  font-style: normal;
  font-size: clamp(32px, 4.6vw, 64px);
  line-height: 1.1;
  letter-spacing: -0.03em;

  &::before {
    content: "“";
    display: block;
    font-style: normal;
    font-size: 1.8em;
    line-height: 0.6;
    height: 0.5em;
    color: rgba(250, 247, 241, 0.45);
  }
`;

const QuoteNote = styled.p`
  position: relative;
  z-index: 1;
  margin-top: 24px;
  font-family: ${fonts.mono};
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(250, 247, 241, 0.75);
`;

const QuoteField = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.35;
  pointer-events: none;
`;

const HowGrid = styled.div`
  display: grid;
  gap: 16px;

  @media ${media.small} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media ${media.large} {
    grid-template-columns: repeat(${(p) => (p.$count > 4 ? 3 : p.$count)}, minmax(0, 1fr));
  }
`;

const HowCard = styled.article`
  padding: 30px 26px;
  border-radius: 24px;
  background: ${colors.linen};
  border: 1px solid ${colors.stone};
  transition: transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.4s ease, border-color 0.4s ease;

  &:hover {
    transform: translateY(-6px);
    border-color: ${(p) => p.$accent};
    box-shadow: 0 30px 60px -40px rgba(42, 35, 30, 0.45);
  }

  i {
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    font-style: normal;
    font-family: ${fonts.mono};
    font-size: 13px;
    color: ${colors.linen};
    background: ${(p) => p.$accent};
  }
  h3 {
    margin: 22px 0 10px;
    font-family: ${fonts.display};
    font-weight: 400;
    font-size: 24px;
    line-height: 1.12;
    letter-spacing: -0.02em;
    color: ${colors.ink};
  }
  p {
    font-family: ${fonts.body};
    font-size: 16px;
    line-height: 1.55;
    color: ${colors.bark};
  }
`;

const Split = styled.div`
  display: grid;
  gap: 24px;
  align-items: start;

  @media ${media.large} {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 32px;
  }
`;

const UsesCard = styled.div`
  padding: 32px;
  border-radius: 28px;
  background: ${colors.sand};

  @media ${media.medium} {
    padding: 40px;
  }

  h3 {
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: clamp(28px, 3vw, 40px);
    letter-spacing: -0.03em;
    color: ${colors.ink};
  }
  ul {
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin: 24px 0 28px;
  }
  li {
    padding: 10px 16px;
    border-radius: 999px;
    background: ${colors.linen};
    border: 1px solid ${colors.stone};
    font-family: ${fonts.body};
    font-size: 15px;
    font-weight: 600;
    color: ${colors.ink};
  }
  p {
    font-family: ${fonts.body};
    font-size: 17px;
    line-height: 1.6;
    color: ${colors.bark};
  }
`;

const ResearchNote = styled.p`
  margin-top: 20px;
  padding: 20px 22px;
  border-radius: 18px;
  border: 1px dashed ${colors.stone};
  font-family: ${fonts.body};
  font-size: 16px;
  line-height: 1.55;
  color: ${colors.bark};
`;

const Others = styled.div`
  display: grid;
  gap: 12px;

  @media ${media.small} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media ${media.large} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const OtherLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 22px 24px;
  border-radius: 20px;
  background: ${colors.linen};
  border: 1px solid ${colors.stone};
  font-family: ${fonts.display};
  font-size: 22px;
  letter-spacing: -0.015em;
  color: ${colors.ink};
  transition: border-color 0.3s ease, transform 0.3s ease;

  &::before {
    content: "";
    flex: none;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${(p) => p.$accent};
  }
  span {
    flex: 1;
  }
  svg {
    color: ${colors.copper};
    transition: transform 0.3s ease;
  }
  &:hover {
    transform: translateY(-3px);
    border-color: ${colors.ink};
  }
  &:hover svg {
    transform: translateX(4px);
  }
`;

const VerticalPage = ({ data }) => {
  const scope = useRef(null);
  const dark = Boolean(data.dark);
  const accent = dark ? colors.copper : data.accent;
  useReveal(scope);

  useLayoutEffect(() => {
    document.title = `vimbal · ${data.nav}`;
    if (prefersReducedMotion()) return undefined;
    const ctx = gsap.context(() => {
      gsap.from("[data-v-hero] > *", { y: 30, opacity: 0, duration: 1, stagger: 0.08, ease: "power3.out" });
      gsap.from("[data-v-visual]", { scale: 0.92, opacity: 0, duration: 1.4, ease: "power3.out", delay: 0.2 });
    }, scope);
    return () => ctx.revert();
  }, [data]);

  const others = verticals.filter((v) => v.slug !== data.slug);

  return (
    <div ref={scope}>
      <Hero $dark={dark} $accent={accent} aria-labelledby="v-title">
        <Container>
          <HeroGrid>
            <div data-v-hero>
              <TopRow>
                <div>
                  <Crumb to="/" $dark={dark}>
                    ← vimbal overview
                  </Crumb>
                  <Eyebrow $color={dark ? colors.sage : accent} $onDark={dark}>
                    {data.eyebrow}
                  </Eyebrow>
                </div>
              </TopRow>
              <HeroTitle id="v-title" $dark={dark} $accent={accent}>
                {data.titleLead} <em>{data.titleEmphasis}</em>
              </HeroTitle>
              <Lead $onDark={dark}>{data.body}</Lead>
              {data.heroFigures ? (
                <Figures>
                  {data.heroFigures.map((f) => (
                    <Figure key={f.label} $dark={dark} $accent={accent}>
                      <b>{f.value}</b>
                      <span>{f.label}</span>
                    </Figure>
                  ))}
                </Figures>
              ) : (
                <Actions>
                  <PrimaryButton to={registerFor(data.slug)}>
                    {data.cta} <Arrow />
                  </PrimaryButton>
                  <Ghost to="/how-it-works" $dark={dark}>
                    How vimbal works
                  </Ghost>
                </Actions>
              )}
            </div>
            <Side>
              {data.heroFigures && (
                <PrimaryButton to={registerFor(data.slug)}>
                  {data.cta} <Arrow />
                </PrimaryButton>
              )}
              <Visual data-v-visual $dark={dark}>
                {data.video ? (
                  <HeroVideo src={data.video} position={data.videoPosition} />
                ) : (
                  <NeuralField variant="dark" interactive calm={0.2} lines={10} />
                )}
                <VisualTag>{data.video ? data.eyebrow : "Move to calm the signal"}</VisualTag>
              </Visual>
            </Side>
          </HeroGrid>

          {data.stats && (
          <StatRow>
            {data.stats.map((s, i) => (
              <Stat key={s.label} $dark={dark} data-reveal={i * 0.08}>
                <b>{s.value}</b>
                <p>{s.label}</p>
                {s.source && <small>Source: {s.source}</small>}
              </Stat>
            ))}
          </StatRow>
          )}
          {data.statsNote && <StatsNote $dark={dark}>{data.statsNote}</StatsNote>}
        </Container>
      </Hero>

      <Band aria-labelledby="v-problem">
        <Container>
          <SectionHead>
            <div data-reveal>
              <Eyebrow $color={colors.terracotta}>The problem</Eyebrow>
              <Display id="v-problem" style={{ marginTop: 18 }}>
                {data.problemTitle}
              </Display>
            </div>
          </SectionHead>
          {data.pairs ? (
            <NdisPairs labels={data.pairLabels} pairs={data.pairs} note={data.pairsNote} />
          ) : (
            <ProblemGrid>
              {data.problems.map((p, i) => (
                <ProblemCard key={p.title} data-reveal={i * 0.08}>
                  <span>0{i + 1}</span>
                  <h3>{p.title}</h3>
                  <p>{p.body}</p>
                </ProblemCard>
              ))}
            </ProblemGrid>
          )}
        </Container>
      </Band>

      {data.chemistry && (
        <Band $pad="0 0 120px" aria-labelledby="v-chem">
          <Container>
            <SectionHead>
              <div data-reveal>
                <Eyebrow $color={colors.ocean}>Inside the brain</Eyebrow>
                <Display id="v-chem" style={{ marginTop: 18 }}>
                  Why focus feels <em>so hard.</em>
                </Display>
              </div>
              <Lead data-reveal="0.1">
                Modern life runs a chemical tug-of-war in your head. Tap each one to see what it does.
              </Lead>
            </SectionHead>
            <div data-reveal>
              <ChemistryExplorer items={chemistry} />
            </div>
          </Container>
        </Band>
      )}

      <QuoteBand $accent={dark ? colors.forestSoft : accent}>
        <QuoteField aria-hidden="true">
          <NeuralField variant="dark" calm={1} lines={7} />
        </QuoteField>
        <Container>
          <BigQuote data-reveal>{data.quote}</BigQuote>
          {data.note && <QuoteNote data-reveal="0.1">{data.note}</QuoteNote>}
        </Container>
      </QuoteBand>


      {data.breathing && (
        <Band $pad="120px 0 120px" aria-labelledby="v-breathe">
          <Container>
            <Split>
              <div data-reveal>
                <Eyebrow $color={colors.moss}>Try it now</Eyebrow>
                <Display id="v-breathe" style={{ margin: "18px 0 20px" }}>
                  One minute of <em>calm.</em>
                </Display>
                <Lead>
                  Slow breathing is one small way to ease the stress response. Inside vimbal, immersion shuts out the room and a patent-filed cognitive engine holds the calm in place while you read.
                </Lead>
                {data.research && <ResearchNote>{data.research}</ResearchNote>}
              </div>
              <div data-reveal="0.1">
                <BreathingOrb />
              </div>
            </Split>
          </Container>
        </Band>
      )}

      {data.resultsQuad ? (
        <Band $pad="0 0 120px" aria-labelledby="v-results">
          <ResultsQuad data={data.resultsQuad} accent={accent} scope={scope} />
        </Band>
      ) : (
        data.pilot && (
          <Band $pad="0 0 120px" aria-label="Pilot results">
            <Container>
              <Split>
                <div data-reveal>
                  <PilotResults data={data.results || pilotResults} accent={accent} />
                </div>
                <div data-reveal="0.1">{data.costCalculator && <CostCalculator accent={accent} />}</div>
              </Split>
            </Container>
          </Band>
        )
      )}

      {data.pilotVoices && <PilotVoices accent={accent} limit={typeof data.pilotVoices === "number" ? data.pilotVoices : undefined} />}

      {data.ndis ? <NdisStory /> : <Voices page={data.slug} />}

      {data.how && (
      <Band $pad="0 0 120px" aria-labelledby="v-how">
        <Container>
          <SectionHead>
            <div data-reveal>
              <Eyebrow $color={colors.moss}>{data.howEyebrow || "How vimbal helps"}</Eyebrow>
              <Display id="v-how" style={{ marginTop: 18 }}>
                {data.howTitle}
              </Display>
            </div>
          </SectionHead>
          <HowGrid $count={data.how.length}>
            {data.how.map((h, i) => (
              <HowCard key={h.title} $accent={accent} data-reveal={(i % 4) * 0.08}>
                <i>0{i + 1}</i>
                <h3>{h.title}</h3>
                <p>{h.body}</p>
              </HowCard>
            ))}
          </HowGrid>
        </Container>
      </Band>
      )}

      {data.ndis && <NdisPlan />}

      <Band $pad="0 0 120px" aria-label="Where vimbal is used">
        <Container>
          <Split>
            {data.calculator && (
              <div data-reveal>
                <ReadingCalculator config={data.calculator} accent={accent} />
              </div>
            )}
            <UsesCard data-reveal="0.1" style={data.calculator ? undefined : { gridColumn: "1 / -1" }}>
              <h3>Where it helps</h3>
              <ul>
                {data.uses.map((u) => (
                  <li key={u}>{u}</li>
                ))}
              </ul>
              <p>{data.delivery}</p>
              {data.research && !data.breathing && <ResearchNote>{data.research}</ResearchNote>}
            </UsesCard>
          </Split>
        </Container>
      </Band>

      <Band $pad="0 0 80px" aria-labelledby="v-others">
        <Container>
          <Eyebrow id="v-others" $color={colors.pebble} style={{ marginBottom: 20 }}>
            Explore other areas
          </Eyebrow>
          <Others>
            {others.map((o) => (
              <OtherLink key={o.slug} to={`/${o.slug}`} $accent={o.dark ? colors.copper : o.accent}>
                <span>{o.nav}</span>
                <Arrow />
              </OtherLink>
            ))}
          </Others>
        </Container>
      </Band>

      <Closing primary={data.cta} interest={data.slug} {...(data.closing || {})} />
    </div>
  );
};

export default VerticalPage;
